import React, { useEffect, useRef, useState, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BREAKPOINTS } from '../consts/app-keys.const';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < BREAKPOINTS.tablet);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < BREAKPOINTS.tablet);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

export interface NavSubItem {
  label: string;
  anchor: string;
}

export interface NavItem {
  label: string;
  to?: string; // absent = group item (no route, just expands/collapses children)
  subItems?: NavSubItem[]; // in-page anchor links (page items only)
  children?: NavItem[]; // child page items (group items only)
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

// Desktop: sticky wrapper pt-[8px] + pill h-[74px] = 82px. Mobile: pill h-[76px] only, no wrapper padding
const HEADER_HEIGHT_DESKTOP = 82;
const HEADER_HEIGHT_MOBILE = 76;
const MOBILE_TOC_TOP = HEADER_HEIGHT_MOBILE;

/**
 * Product-agnostic docs shell: sticky sidebar nav (sections, expandable groups,
 * page items, in-page anchor sub-items), scroll-spy, mobile drawer, and
 * hash-anchor scrolling. Each product passes its own `navSections` config; the
 * page bodies render through <Outlet />.
 */
const DocsLayout: React.FC<{ navSections: NavSection[] }> = ({ navSections }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);

  const expandItem = (to: string) => {
    setExpanded((prev) => ({ ...prev, [to]: true }));
  };

  const toggleItem = (to: string) => {
    setExpanded((prev) => ({ ...prev, [to]: !prev[to] }));
  };

  // Auto-expand the active page and any group ancestors on navigation
  useEffect(() => {
    const expandActive = (items: NavItem[]) => {
      for (const item of items) {
        if (item.to === location.pathname) {
          expandItem(item.to);
        }
        if (item.children) {
          if (item.children.some((c) => c.to === location.pathname)) {
            expandItem(item.label); // expand the parent group
          }
          expandActive(item.children);
        }
      }
    };
    navSections.forEach((section) => expandActive(section.items));
    setActiveAnchor(null);
    setMobileNavOpen(false);
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: effect is intentionally keyed to the route only
  }, [location.pathname]);

  // Scroll spy — highlights the active anchor as the user scrolls
  const getPageAnchors = useCallback((): string[] => {
    const search = (items: NavItem[]): NavSubItem[] | null => {
      for (const item of items) {
        if (item.to === location.pathname) return item.subItems ?? [];
        if (item.children) {
          const found = search(item.children);
          if (found !== null) return found;
        }
      }
      return null;
    };
    for (const section of navSections) {
      const result = search(section.items);
      if (result !== null) return result.map((s) => s.anchor);
    }
    return [];
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: effect is intentionally keyed to the route only
  }, [location.pathname]);

  useEffect(() => {
    const anchors = getPageAnchors();
    if (!anchors.length) return;

    const handleScroll = () => {
      // If scrolled to the bottom, the last anchor wins unconditionally
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) {
        setActiveAnchor(anchors[anchors.length - 1]);
        return;
      }
      const headerHeight = isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP;
      const activationLine = headerHeight + 100;
      let active: string | null = null;
      for (const anchor of anchors) {
        const el = document.getElementById(anchor);
        if (el && el.getBoundingClientRect().top <= activationLine) active = anchor;
      }
      setActiveAnchor(active);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, getPageAnchors, isMobile]);

  const scrollToAnchor = (anchor: string) => {
    const el = document.getElementById(anchor);
    if (el) {
      // scroll-padding-top on <html> (see index.css) keeps the heading clear
      // of the sticky header.
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Scroll to hash anchor after navigation (including cross-page links)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }

    const anchor = location.hash.slice(1);
    let cancelled = false;

    // Cross-page anchor jumps used to land a few hundred pixels off on the
    // first click because the scroll position was computed before the page's
    // images had loaded — once they decoded, the layout shifted and our
    // target was no longer where we'd scrolled to. Wait for any unloaded
    // images inside the docs container so the bounding rects we read reflect
    // final layout, with a hard 1.5s cap so the scroll never hangs on a
    // missing/slow asset.
    const waitForImagesToSettle = (root: HTMLElement) =>
      new Promise<void>((resolve) => {
        const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
        const pending = imgs.filter((img) => !img.complete);
        if (pending.length === 0) return resolve();
        let remaining = pending.length;
        const done = () => {
          if (--remaining <= 0) resolve();
        };
        pending.forEach((img) => {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
        setTimeout(resolve, 1500);
      });

    const tryScroll = async (tries: number) => {
      if (cancelled) return;
      const el = document.getElementById(anchor);
      const container = mainRef.current;
      if (!el || !container) {
        if (tries > 0) setTimeout(() => tryScroll(tries - 1), 80);
        return;
      }
      await waitForImagesToSettle(container);
      if (cancelled) return;
      // rAF lets the browser finish layout before scrolling
      requestAnimationFrame(() => scrollToAnchor(anchor));
    };
    tryScroll(5);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: effect is intentionally keyed to the route only
  }, [location.pathname, location.hash]);

  const renderNavItem = (item: NavItem): React.ReactNode => {
    const key = item.to ?? item.label;
    const isActive = !!item.to && location.pathname === item.to;
    const isExpanded = expanded[key] ?? false;
    const hasExpandable = !!(item.subItems?.length || item.children?.length);
    const isGroup = !item.to;

    return (
      <div key={key}>
        {/* Row */}
        <div className={`flex items-center border-r-2 ${isActive ? 'border-r-blue-link' : 'border-r-transparent'}`}>
          {hasExpandable ? (
            <span
              onClick={() => toggleItem(key)}
              className="pl-[14px] pr-[4px] text-slate-400 leading-none select-none cursor-pointer flex items-center shrink-0"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className={`transition-transform duration-150 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
              >
                <path
                  d="M2 4l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ) : (
            <span className="pl-[30px]" />
          )}

          {isGroup ? (
            // Group item — no route, just toggles expand/collapse
            <button
              onClick={() => toggleItem(key)}
              className="flex-1 text-left px-[8px] pl-[6px] py-[7px] text-[14px] font-medium text-content-secondary bg-transparent border-none cursor-pointer transition-colors duration-100"
            >
              {item.label}
            </button>
          ) : (
            // Page item — navigates
            <NavLink
              to={item.to!}
              onClick={() => (isActive ? toggleItem(key) : expandItem(key))}
              className={`flex-1 block px-[8px] pl-[6px] py-[7px] text-[14px] bg-transparent no-underline transition-colors duration-100 cursor-pointer
                ${isActive ? 'font-semibold text-blue-link' : 'font-regular text-content-secondary'}`}
            >
              {item.label}
            </NavLink>
          )}
        </div>

        {/* Child nav items (group children) */}
        {item.children && isExpanded && (
          <div className="ml-[20px] border-l border-surface-hover pl-[4px] mb-[4px]">
            {item.children.map((child) => renderNavItem(child))}
          </div>
        )}

        {/* In-page anchor sub-items */}
        {item.subItems && isExpanded && (
          <div className="ml-[20px] border-l border-surface-hover pl-[16px] mb-[4px]">
            {item.subItems.map((sub) => (
              <button
                key={sub.anchor}
                onClick={() => {
                  navigate(item.to! + '#' + sub.anchor);
                  // If same page + same hash, navigate is a no-op so the effect won't fire — scroll directly
                  if (location.pathname === item.to && location.hash === '#' + sub.anchor) {
                    scrollToAnchor(sub.anchor);
                  }
                  setMobileNavOpen(false);
                }}
                className={`block w-full text-left pr-[20px] py-[5px] text-[13px] border-none cursor-pointer transition-colors duration-100 bg-transparent hover:text-blue-link
                  ${activeAnchor === sub.anchor ? 'font-medium text-blue-link' : 'font-regular text-slate-500'}`}
              >
                {sub.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      {navSections.map((section) => (
        <div key={section.title} className="mb-[24px]">
          <div className="text-[11px] font-bold tracking-[0.08em] text-slate-400 uppercase px-[20px] pb-[8px]">
            {section.title}
          </div>
          {section.items.map((item) => renderNavItem(item))}
        </div>
      ))}
    </>
  );

  return (
    <div className="flex font-body items-start">
      {/* Desktop sidebar — sticky below the site header, scrolls if taller than viewport */}
      {!isMobile && (
        <aside
          className="themed-scrollbar w-[240px] shrink-0 border-r border-surface-hover pt-[32px] pb-[40px] sticky overflow-y-auto"
          style={{
            top: `${HEADER_HEIGHT_DESKTOP}px`,
            maxHeight: `calc(100vh - ${HEADER_HEIGHT_DESKTOP}px)`
          }}
        >
          {sidebarContent}
        </aside>
      )}

      {/* Mobile: overlay backdrop */}
      {isMobile && mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 bg-border-dark/35 z-40"
        />
      )}

      {/* Mobile: slide-in drawer */}
      {isMobile && (
        <aside
          className={`themed-scrollbar fixed top-0 left-0 w-[260px] h-full bg-surface-base z-50 overflow-y-auto pt-[32px] pb-[40px] border-r border-surface-hover transition-transform duration-[220ms] ease-in-out
            ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          {/* Close button */}
          <button
            onClick={() => setMobileNavOpen(false)}
            className="absolute top-[12px] right-[12px] bg-transparent border-none cursor-pointer text-slate-400 p-[4px]"
            aria-label={t('docs.layout.closeNavigation')}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M2 2l14 14M16 2L2 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {sidebarContent}
        </aside>
      )}

      {/* Content area — flows naturally; the page (window) is the scroll container */}
      <main
        ref={mainRef}
        className="flex-1 max-w-[860px] min-w-0"
        style={{ padding: isMobile ? '24px 20px' : '40px 56px' }}
      >
        {/* Mobile TOC toggle — fixed at top so nothing scrolls above it */}
        {isMobile && (
          <>
            <div
              className="fixed left-0 right-0 z-30 bg-surface-base px-[20px] py-[12px] border-b border-surface-hover box-border"
              style={{ top: `${MOBILE_TOC_TOP}px` }}
            >
              <button
                onClick={() => setMobileNavOpen(true)}
                className="flex items-center gap-[6px] px-[12px] py-[6px] text-[13px] font-medium text-blue-link bg-blue-50 border border-border-brand rounded-[6px] cursor-pointer"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M1 3h12M1 7h12M1 11h7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {t('docs.layout.contents')}
              </button>
            </div>
            {/* Spacer so content doesn't hide under fixed TOC */}
            <div className="h-[52px] shrink-0" />
          </>
        )}
        <Outlet />
      </main>
    </div>
  );
};

export default DocsLayout;
